// Formatting shared by the Network Diagnostics list and detail panes.

export const formatBytes = (bytes) => {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const formatDuration = (ms) => {
    if (ms == null) return "—";
    if (ms < 1000) return `${Math.round(ms)} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
};

// Wall-clock time of the request, to the millisecond (requests often come in bursts).
export const formatClock = (timestamp) => {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    const pad = (n, width = 2) => String(n).padStart(width, "0");
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
};

// Colour band for the status pill / row: pending, network failure, 2xx, 3xx, 4xx, 5xx.
export const statusClass = (entry) => {
    if (entry.error) return "netdiag-status-failed";
    if (entry.pending) return "netdiag-status-pending";
    const status = entry.status ?? 0;
    if (status >= 500) return "netdiag-status-5xx";
    if (status >= 400) return "netdiag-status-4xx";
    if (status >= 300) return "netdiag-status-3xx";
    if (status >= 200) return "netdiag-status-2xx";
    return "netdiag-status-failed";
};

export const statusLabel = (entry) => {
    if (entry.pending) return "pending";
    if (entry.error) return "failed";
    return String(entry.status ?? "—");
};

// The path (plus query) is what identifies a call day to day; the host goes in the subtitle.
export const splitUrl = (url) => {
    try {
        const parsed = new URL(url, globalThis.location?.href);
        return { host: parsed.host, path: `${parsed.pathname}${parsed.search}` };
    } catch {
        return { host: "", path: url };
    }
};

export const shortContentType = (contentType) => (contentType ?? "").split(";")[0] || "—";

// Text for the clipboard. JSON bodies are re-serialized from the parsed (secret-redacted) tree
// so copying can't leak a token the panel itself masks; other bodies are copied verbatim.
export const bodyToText = (body) => {
    if (!body) return "";
    return body.json === null || body.json === undefined ? (body.text ?? "") : JSON.stringify(body.json, null, 2);
};

// What "Copy entry" puts on the clipboard: the whole request, secrets already redacted.
export const entryToJson = (entry) => JSON.stringify({
    method: entry.method,
    url: entry.url,
    status: entry.status,
    statusText: entry.statusText,
    error: entry.error,
    startedAt: new Date(entry.startedAt).toISOString(),
    durationMs: entry.durationMs,
    size: entry.size,
    requestHeaders: entry.requestHeaders,
    requestBody: entry.request?.json ?? bodyToText(entry.request) ?? null,
    responseHeaders: entry.responseHeaders,
    responseBody: entry.response?.json ?? bodyToText(entry.response) ?? null,
}, null, 2);
